import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
/* eslint-disable no-console */
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useVoiceRecognition } from './useVoiceRecognition';
import { useWakeWord } from './useWakeWord';
import { normalizeVoiceCommand, getCommandSuggestions, isVoiceNavigationDisabled } from './voiceCommands';
import { checkModuleOrSubmodulePermission } from '../../utils/permissionService';
import type {
  NormalizedCommand,
  VoiceCommand,
  VoiceNavigationState,
  VoiceNavigationLog,
  VoiceNavigationContextValue,
  RouteCategory,
} from './types';

// Re-export types for backward compatibility
export type { VoiceNavigationState, VoiceNavigationLog, VoiceNavigationContextValue } from './types';

// ============================================================================
// Permission Mapping
// Maps route categories to permission module names
// ============================================================================

const CATEGORY_TO_PERMISSION_MODULE: Record<RouteCategory, string> = {
  admin: 'Admin',
  clinical: 'Clinical',
  lab: 'Lab',
  billing: 'Admin', // Billing is under Admin module
  inventory: 'Admin', // Inventory is under Admin module
  configuration: 'Settings',
  ai: 'Clinical', // AI features are under Clinical
  reports: 'Admin', // Reports are under Admin
};

/**
 * Check if the user has permission to navigate to a route based on its category
 * @param category - The route category
 * @returns true if the user has permission, false otherwise
 */
function checkRoutePermission(category: RouteCategory | undefined): boolean {
  // If no category specified, allow navigation (for backward compatibility)
  if (!category) {
    console.log('[VoiceNav] No category specified, allowing navigation');
    return true;
  }

  const moduleName = CATEGORY_TO_PERMISSION_MODULE[category];
  if (!moduleName) {
    console.log('[VoiceNav] Unknown category:', category, 'allowing navigation');
    return true;
  }

  const result = checkModuleOrSubmodulePermission(moduleName);
  const hasPermission = result?.AllowPermissions ?? false;
  
  console.log('[VoiceNav] Permission check:', {
    category,
    moduleName,
    hasPermission,
  });

  return hasPermission;
}

// Hook to get patient ID from URL params only (safe, no context dependency)
function usePatientIdFromUrl(): number {
  const params = useParams<{ id?: string }>();
  
  // Try to get from URL params
  if (params.id) {
    const urlId = parseInt(params.id, 10);
    if (!isNaN(urlId) && urlId > 0) {
      return urlId;
    }
  }
  
  return -1;
}

// ============================================================================
// Context
// ============================================================================

const VoiceNavigationContext = createContext<VoiceNavigationContextValue | null>(null);

/**
 * Hook to access voice navigation functionality.
 * 
 * Provides access to the current voice navigation state, matched commands,
 * suggestions, and control methods for managing voice-based navigation.
 * 
 * Must be used within a VoiceNavigationProvider.
 * 
 * @returns The voice navigation context value
 * @throws Error if used outside of VoiceNavigationProvider
 * 
 * @example
 * ```tsx
 * const { 
 *   state, 
 *   transcript, 
 *   matchedCommand, 
 *   startListening, 
 *   stopListening 
 * } = useVoiceNavigation();
 * 
 * return (
 *   <button onClick={startListening}>
 *     {state === 'listening' ? 'Listening...' : 'Start Voice Nav'}
 *   </button>
 * );
 * ```
 */
export function useVoiceNavigation(): VoiceNavigationContextValue {
  const context = useContext(VoiceNavigationContext);
  if (!context) {
    throw new Error('useVoiceNavigation must be used within a VoiceNavigationProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface VoiceNavigationProviderProps {
  /** Child components that will have access to voice navigation */
  children: ReactNode;
  /** Maximum number of navigation logs to keep (default: 50) */
  maxLogs?: number;
  /** Whether to enable wake word detection (default: true) */
  enableWakeWord?: boolean;
}

/**
 * Provider component that enables voice navigation throughout the app.
 * 
 * Wraps the application with voice navigation context, enabling:
 * - Wake word detection ("Hey Vidai")
 * - Voice command recognition for navigation
 * - Automatic permission checking before navigation
 * - Navigation history logging
 * 
 * @param props - Provider configuration
 * @param props.children - Child components
 * @param props.maxLogs - Maximum logs to retain (default: 50)
 * @param props.enableWakeWord - Enable wake word detection (default: true)
 * 
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <VoiceNavigationProvider enableWakeWord={true}>
 *       <AppContent />
 *       <VoiceActivationOverlay />
 *     </VoiceNavigationProvider>
 *   );
 * }
 * ```
 */
export const VoiceNavigationProvider: React.FC<VoiceNavigationProviderProps> = ({
  children,
  maxLogs = 50,
  enableWakeWord = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if voice navigation should be disabled on current route
  const isDisabledRoute = isVoiceNavigationDisabled(location.pathname);
  
  // Get patient ID from URL params (most reliable when on a patient page)
  const patientId = usePatientIdFromUrl();
  
  const [state, setState] = useState<VoiceNavigationState>('idle');
  const [matchedCommand, setMatchedCommand] = useState<NormalizedCommand | null>(null);
  const [suggestions, setSuggestions] = useState<VoiceCommand[]>([]);
  const [logs, setLogs] = useState<VoiceNavigationLog[]>([]);
  // Read wake word enabled state from localStorage, respecting user settings
  const [wakeWordEnabled, setWakeWordEnabled] = useState(() => {
    if (typeof window === 'undefined') return enableWakeWord;
    return localStorage.getItem('voiceNavigationEnabled') === 'true';
  });
  const [displayError, setDisplayError] = useState<string | null>(null);
  const [isCommandListening, setIsCommandListening] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startCommandListeningRef = useRef<(() => void) | null>(null);
  const processedFinalResultRef = useRef(false);
  const lastInterimTranscriptRef = useRef<string>('');
  const startListeningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isStartingListeningRef = useRef(false);
  const startRecognitionRef = useRef<(() => void) | null>(null);
  const resetTranscriptRef = useRef<(() => void) | null>(null);
  const stopRecognitionRef = useRef<(() => void) | null>(null);
  const startWakeWordListeningRef = useRef<(() => void) | null>(null);

  // Add log entry
  const addLog = useCallback((log: Omit<VoiceNavigationLog, 'id' | 'timestamp'>) => {
    const newLog: VoiceNavigationLog = {
      ...log,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      timestamp: new Date(),
    };
    setLogs(prev => [newLog, ...prev].slice(0, maxLogs));
  }, [maxLogs]);

  // Handle speech transcript logging
  const handleTranscript = useCallback((transcript: string, isFinal: boolean) => {
    if (transcript.trim()) {
      addLog({
        transcript: transcript,
        normalizedCommand: isFinal ? 'Speech detected (final)' : 'Speech detected (interim)',
        route: null,
        success: true,
      });
    }
  }, [addLog]);

  // Handle wake word detection
  const handleWakeWordDetected = useCallback((detectedWord: string) => {
    // Don't activate on disabled routes
    if (isDisabledRoute) {
      console.log('[VoiceNavigationProvider] Voice navigation disabled on this route');
      return;
    }
    
    // Wake word detected - open panel instantly and start command listening
    console.log('[VoiceNavigationProvider] Wake word detected:', detectedWord);
    
    // Immediately show the listening panel
    setState('listening');
    setIsCommandListening(true);
    
    // Start command listening (has its own delay for mic release)
    startCommandListeningRef.current?.();
    
    addLog({
      transcript: detectedWord,
      normalizedCommand: 'Wake word detected',
      route: null,
      success: true,
    });
  }, [addLog, isDisabledRoute]);

  // Handle wake word errors
  const handleWakeWordError = useCallback((error: string) => {
    addLog({
      transcript: error,
      normalizedCommand: 'Wake word error',
      route: null,
      success: false,
      errorMessage: error,
    });
  }, [addLog]);

  // Ref for performNavigation to use in handleCommandDetected before it's defined
  const performNavigationRef = useRef<((command: NormalizedCommand, transcript: string) => void) | null>(null);

  // Handle direct command detection (navigation without wake word)
  const handleCommandDetected = useCallback((command: NormalizedCommand, transcript: string) => {
    // Don't process commands on disabled routes
    if (isDisabledRoute) {
      console.log('[VoiceNavigationProvider] Voice navigation disabled on this route');
      return;
    }
    
    console.log('[VoiceNavigationProvider] Direct command detected:', command.label);
    
    // Show the listening panel briefly with the matched command, then navigate
    setState('processing');
    setIsCommandListening(false);
    setMatchedCommand(command);
    setSuggestions([]);
    
    addLog({
      transcript: transcript,
      normalizedCommand: command.label,
      route: command.route,
      success: true,
    });
    
    // Navigate directly using performNavigation ref
    // Use a small timeout to show the command briefly
    setTimeout(() => {
      performNavigationRef.current?.(command, transcript);
    }, 200);
  }, [addLog, isDisabledRoute]);

  // Wake word hook - listens for "Hey Vidai" and also valid navigation commands
  const {
    status: wakeWordStatus,
    isSupported: wakeWordSupported,
    error: wakeWordError,
    startListening: startWakeWordListening,
    stopListening: stopWakeWordListening,
  } = useWakeWord({
    enabled: wakeWordEnabled && !isCommandListening && !isDisabledRoute,
    onWakeWordDetected: handleWakeWordDetected,
    onCommandDetected: handleCommandDetected,
    onTranscript: handleTranscript,
    onError: handleWakeWordError,
    detectCommands: true, // Enable instant command detection
  });

  // Update wake word ref
  React.useEffect(() => {
    startWakeWordListeningRef.current = startWakeWordListening;
  }, [startWakeWordListening]);

  // Log wake word status changes for debugging
  React.useEffect(() => {
    if (wakeWordStatus === 'listening') {
      addLog({
        transcript: 'Wake word listening started',
        normalizedCommand: 'System',
        route: null,
        success: true,
      });
    } else if (wakeWordStatus === 'error' && wakeWordError) {
      addLog({
        transcript: wakeWordError,
        normalizedCommand: 'Wake word error',
        route: null,
        success: false,
      });
    }
  }, [wakeWordStatus, wakeWordError, addLog]);

  // Listen for voice navigation toggle from settings
  React.useEffect(() => {
    const handleStorageChange = () => {
      const isEnabled = localStorage.getItem('voiceNavigationEnabled') === 'true';
      setWakeWordEnabled(isEnabled);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update state when wake word is listening
  React.useEffect(() => {
    if (wakeWordStatus === 'listening' && state === 'idle' && wakeWordEnabled) {
      setState('wake-word-listening');
    }
  }, [wakeWordStatus, state, wakeWordEnabled]);

  // Clear timeout helper
  const clearStateTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Resolve route with patient ID if needed
  const resolveRoute = useCallback((command: NormalizedCommand): string => {
    let route = command.route;
    
    // If route requires patient ID
    if (command.requiresPatientId) {
      if (patientId > 0) {
        // Replace :id placeholder with actual patient ID
        route = route.replace(':id', String(patientId));
      } else {
        // No patient selected, use fallback path
        route = command.fallbackPath || '/clinical/patients';
        console.log('[VoiceNav] No patient selected, redirecting to:', route);
      }
    }
    
    return route;
  }, [patientId]);

  // Handle successful navigation
  const performNavigation = useCallback((command: NormalizedCommand, transcript: string) => {
    const route = resolveRoute(command);
    const label = command.label;
    
    // Check permission before navigation
    if (!checkRoutePermission(command.category)) {
      setState('error');
      setDisplayError(`Access denied: You don't have permission to access ${label}`);
      setIsCommandListening(false);
      stopRecognitionRef.current?.();
      addLog({
        transcript,
        normalizedCommand: label,
        route,
        success: false,
        errorMessage: 'Permission denied',
      });
      
      // Resume wake word listening after error display
      timeoutRef.current = setTimeout(() => {
        setState(wakeWordEnabled ? 'wake-word-listening' : 'idle');
        setDisplayError(null);
        if (wakeWordEnabled) {
          startWakeWordListeningRef.current?.();
        }
      }, 2000);
      return;
    }
    
    // Don't navigate if already on the same route
    if (location.pathname === route) {
      setState('success');
      setIsCommandListening(false);
      stopRecognitionRef.current?.();
      addLog({
        transcript,
        normalizedCommand: label,
        route,
        success: true,
      });
      
      // Close panel after brief success display and restart wake word
      timeoutRef.current = setTimeout(() => {
        setState(wakeWordEnabled ? 'wake-word-listening' : 'idle');
        // Restart wake word listener immediately
        if (wakeWordEnabled) {
          startWakeWordListeningRef.current?.();
        }
      }, 1000);
      return;
    }

    try {
      navigate(route);
      setState('success');
      setIsCommandListening(false);
      stopRecognitionRef.current?.();
      addLog({
        transcript,
        normalizedCommand: label,
        route,
        success: true,
      });
      
      // Close panel after brief success display and restart wake word
      timeoutRef.current = setTimeout(() => {
        setState(wakeWordEnabled ? 'wake-word-listening' : 'idle');
        // Restart wake word listener immediately
        if (wakeWordEnabled) {
          startWakeWordListeningRef.current?.();
        }
      }, 1000);
    } catch {
      // Navigation failed - show error briefly then continue listening
      setState('error');
      setDisplayError('Navigation failed. Please try again.');
      addLog({
        transcript,
        normalizedCommand: label,
        route,
        success: false,
        errorMessage: 'Navigation failed',
      });
      
      // Keep listening - show error briefly then resume
      timeoutRef.current = setTimeout(() => {
        setState('listening');
        setDisplayError(null);
        resetTranscriptRef.current?.();
        processedFinalResultRef.current = false;
        lastInterimTranscriptRef.current = '';
        startRecognitionRef.current?.();
      }, 1000);
    }
  }, [navigate, location.pathname, addLog, wakeWordEnabled, resolveRoute]);

  // Update performNavigationRef after performNavigation is defined
  React.useEffect(() => {
    performNavigationRef.current = performNavigation;
  }, [performNavigation]);

  // Ref to track if we already navigated (to prevent duplicate navigation from interim + final)
  const hasNavigatedRef = useRef(false);

  // Process voice result - handles both interim and final results
  const handleVoiceResult = useCallback((transcript: string, isFinal: boolean) => {
    // Skip if we already navigated successfully
    if (hasNavigatedRef.current && !isFinal) {
      return;
    }

    const normalized = normalizeVoiceCommand(transcript);

    // For interim results, only navigate if we have a HIGH confidence match (>=0.85)
    if (!isFinal) {
      if (normalized && normalized.confidence >= 0.85) {
        console.log('[VoiceNav] Instant match on interim:', normalized.label, 'confidence:', normalized.confidence);
        hasNavigatedRef.current = true;
        setState('processing');
        clearStateTimeout();
        setMatchedCommand(normalized);
        setSuggestions([]);
        
        if (normalized.requiresConfirmation) {
          setState('confirming');
        } else {
          performNavigation(normalized, transcript);
        }
      }
      // For low confidence interim results, just update UI without navigating
      return;
    }

    // For final results, reset the navigation flag for next command
    hasNavigatedRef.current = false;

    setState('processing');
    clearStateTimeout();

    if (normalized) {
      setMatchedCommand(normalized);
      setSuggestions([]);

      if (normalized.requiresConfirmation) {
        setState('confirming');
      } else {
        performNavigation(normalized, transcript);
      }
    } else {
      // No match found - show briefly then continue listening
      const commandSuggestions = getCommandSuggestions(transcript);
      setSuggestions(commandSuggestions);
      setMatchedCommand(null);
      setState('not-found');
      
      addLog({
        transcript,
        normalizedCommand: null,
        route: null,
        success: false,
        errorMessage: 'Command not recognized',
      });

      // Show not-found briefly (800ms), then go back to listening
      timeoutRef.current = setTimeout(() => {
        // Reset and continue listening - don't close panel
        setSuggestions([]);
        setState('listening');
        resetTranscriptRef.current?.();
        processedFinalResultRef.current = false;
        lastInterimTranscriptRef.current = '';
        // Force restart recognition
        stopRecognitionRef.current?.();
        setTimeout(() => {
          startRecognitionRef.current?.();
        }, 100);
      }, 800);
    }
  }, [performNavigation, clearStateTimeout, addLog]);

  // Voice recognition hook
  const {
    status: recognitionStatus,
    transcript,
    interimTranscript,
    error: recognitionError,
    isSupported,
    startListening: startRecognition,
    stopListening: stopRecognition,
    resetTranscript,
  } = useVoiceRecognition({
    language: 'en-US',
    continuous: true,  // Keep listening for multiple commands
    interimResults: true,
    onResult: (result) => {
      // Track interim transcript for fallback processing
      if (!result.isFinal) {
        lastInterimTranscriptRef.current = result.transcript;
        // Process interim results for instant matching
        handleVoiceResult(result.transcript, false);
      }
      
      if (result.isFinal) {
        processedFinalResultRef.current = true;
        handleVoiceResult(result.transcript, true);
      }
    },
    onEnd: () => {
      // If recognition ended without a final result, process the last interim transcript
      if (!processedFinalResultRef.current && lastInterimTranscriptRef.current.trim()) {
        console.log('[VoiceNavigationProvider] No final result, processing interim:', lastInterimTranscriptRef.current);
        handleVoiceResult(lastInterimTranscriptRef.current, true);
      } else if (state === 'listening' && isCommandListening) {
        // Recognition ended unexpectedly while we should be listening - restart
        console.log('[VoiceNavigationProvider] Recognition ended unexpectedly, restarting...');
        setTimeout(() => {
          if (isCommandListening && state === 'listening') {
            startRecognitionRef.current?.();
          }
        }, 100);
      }
    },
    onError: (error) => {
      // Don't show error state for minor issues, just restart listening
      console.log('[VoiceNavigationProvider] Recognition error:', error);
      
      addLog({
        transcript: transcript || interimTranscript || '',
        normalizedCommand: null,
        route: null,
        success: false,
        errorMessage: error,
      });
      
      // If we should still be listening, restart automatically
      if (isCommandListening) {
        setState('listening');
        setTimeout(() => {
          startRecognitionRef.current?.();
        }, 200);
      }
    },
  });

  // Update refs for use in callbacks that are defined before the hook
  React.useEffect(() => {
    startRecognitionRef.current = startRecognition;
    resetTranscriptRef.current = resetTranscript;
    stopRecognitionRef.current = stopRecognition;
  }, [startRecognition, resetTranscript, stopRecognition]);

  // Sync recognition status with state
  React.useEffect(() => {
    if (recognitionStatus === 'listening' && state === 'idle') {
      setState('listening');
    }
  }, [recognitionStatus, state]);

  // Start listening
  const startListening = useCallback(() => {
    // Don't start listening on disabled routes
    if (isDisabledRoute) {
      console.log('[VoiceNavigationProvider] Voice navigation disabled on this route');
      return;
    }
    
    console.log('[VoiceNavigationProvider] startListening called');
    
    // Prevent duplicate calls while we're already starting
    if (isStartingListeningRef.current) {
      console.log('[VoiceNavigationProvider] Already starting listening, skipping duplicate call');
      return;
    }
    
    // Clear any pending start timeout
    if (startListeningTimeoutRef.current) {
      clearTimeout(startListeningTimeoutRef.current);
      startListeningTimeoutRef.current = null;
    }
    
    isStartingListeningRef.current = true;
    
    clearStateTimeout();
    setMatchedCommand(null);
    setSuggestions([]);
    setDisplayError(null);
    resetTranscript();
    
    // Reset tracking refs for new listening session
    processedFinalResultRef.current = false;
    lastInterimTranscriptRef.current = '';
    hasNavigatedRef.current = false;  // Reset navigation flag for new session
    
    // Stop wake word listener first and mark as command listening
    setIsCommandListening(true);
    stopWakeWordListening();
    
    // Set listening state immediately for UI feedback
    setState('listening');
    
    console.log('[VoiceNavigationProvider] Wake word stopped, starting command recognition...');
    
    // Quick delay for wake word to release microphone
    startListeningTimeoutRef.current = setTimeout(() => {
      console.log('[VoiceNavigationProvider] Calling startRecognition now');
      startRecognition();
      isStartingListeningRef.current = false;
    }, 300);
  }, [startRecognition, resetTranscript, clearStateTimeout, stopWakeWordListening, isDisabledRoute]);

  // Store ref to startListening for wake word callback
  React.useEffect(() => {
    startCommandListeningRef.current = startListening;
  }, [startListening]);

  // Stop listening and restart wake word
  const stopListening = useCallback(() => {
    stopRecognition();
    setIsCommandListening(false);
    isStartingListeningRef.current = false;
    if (startListeningTimeoutRef.current) {
      clearTimeout(startListeningTimeoutRef.current);
      startListeningTimeoutRef.current = null;
    }
    if (state === 'listening') {
      setState('processing');
    }
  }, [stopRecognition, state]);

  // Confirm navigation (for sensitive routes)
  const confirmNavigation = useCallback(() => {
    if (matchedCommand) {
      performNavigation(matchedCommand, transcript);
    }
  }, [matchedCommand, performNavigation, transcript]);

  // Cancel navigation
  const cancelNavigation = useCallback(() => {
    clearStateTimeout();
    if (startListeningTimeoutRef.current) {
      clearTimeout(startListeningTimeoutRef.current);
      startListeningTimeoutRef.current = null;
    }
    isStartingListeningRef.current = false;
    stopRecognition();
    setMatchedCommand(null);
    setSuggestions([]);
    setIsCommandListening(false);
    setState(wakeWordEnabled ? 'wake-word-listening' : 'idle');
  }, [clearStateTimeout, wakeWordEnabled, stopRecognition]);

  // Navigate to suggestion
  const navigateToSuggestion = useCallback((command: VoiceCommand) => {
    clearStateTimeout();
    // Convert VoiceCommand to NormalizedCommand format for performNavigation
    const normalizedCommand: NormalizedCommand = {
      command: command.label,
      route: command.route,
      label: command.label,
      confidence: 1,
      requiresConfirmation: command.requiresConfirmation || false,
      requiresPatientId: command.requiresPatientId,
      fallbackPath: command.fallbackPath,
    };
    performNavigation(normalizedCommand, transcript || command.label);
    setSuggestions([]);
    setIsCommandListening(false);
  }, [performNavigation, transcript, clearStateTimeout]);

  // Reset everything
  const reset = useCallback(() => {
    clearStateTimeout();
    resetTranscript();
    setMatchedCommand(null);
    setSuggestions([]);
    setDisplayError(null);
    setIsCommandListening(false);
    setState(wakeWordEnabled ? 'wake-word-listening' : 'idle');
  }, [resetTranscript, clearStateTimeout, wakeWordEnabled]);

  // Toggle wake word
  const toggleWakeWord = useCallback(() => {
    setWakeWordEnabled(prev => {
      const newValue = !prev;
      if (newValue) {
        startWakeWordListening();
        setState('wake-word-listening');
      } else {
        stopWakeWordListening();
        setState('idle');
      }
      return newValue;
    });
  }, [startWakeWordListening, stopWakeWordListening]);

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // Restart wake word after command processing
  React.useEffect(() => {
    if (
      (state === 'success' || state === 'error' || state === 'not-found') &&
      wakeWordEnabled &&
      !isCommandListening
    ) {
      const timeout = setTimeout(() => {
        setState('wake-word-listening');
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [state, wakeWordEnabled, isCommandListening]);

  const value: VoiceNavigationContextValue = {
    state,
    isSupported: isSupported || wakeWordSupported,
    transcript,
    interimTranscript,
    error: displayError || recognitionError,
    matchedCommand,
    suggestions,
    logs,
    wakeWordEnabled,
    startListening,
    stopListening,
    confirmNavigation,
    cancelNavigation,
    navigateToSuggestion,
    reset,
    clearLogs,
    toggleWakeWord,
  };

  return (
    <VoiceNavigationContext.Provider value={value}>
      {children}
    </VoiceNavigationContext.Provider>
  );
};

export default VoiceNavigationProvider;
